import { SearchField } from "@/components/common/SearchField/SearchField"
import { toast } from "@/components/common/Toast"
import { FacetedFilter } from "@/components/feature/KnowledgebaseFilters/FacetedFilter"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { PERMISSIONS } from "@/constants/permissions"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { cn } from "@/lib/utils"
import {
  ArrowUpDown,
  CheckCircle,
  Clock,
  FileText,
  Filter,
  Loader2,
  RefreshCw,
  Trash2,
} from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router"
import { toastApiError } from "../../api/apiError"
import {
  pinInFlightRows,
  useDeclarationFacets,
  useDeclarationStats,
  useDeclarationsWithPolling,
  useRetryDeclaration,
} from "../../api/declarationsApi"
import { isPostApproval, LIFECYCLE_OPTIONS } from "../../api/lifecycle"
import type {
  Declaration,
  DeclarationPattern,
  DeclarationSortBy,
  DeclarationSortDir,
} from "../../api/types"
import { useMcTableColumnWidths } from "../../hooks/useMcTableColumnWidths"
import { formatDate } from "../../utils/date"
import { regulationLabel, regulationLabels } from "../../utils/regulationLabel"
import { useNumberFormat } from "../home/useNumberFormat"
import { holdReasonOf } from "../shared/holdReason"
import { ColumnResizeHandle } from "../shared/McTable"
import { RoundtripHoldPill } from "../shared/RoundtripHoldPill"
import { StatGrid } from "../shared/StatGrid"
import { DeclarationDeleteDialog } from "./DeclarationDeleteDialog"
import { useDeclarationLifecycleToasts } from "./hooks/useDeclarationLifecycleToasts"
import {
  DATE_SORT_KEY,
  listParamsFromSearch,
  sanitizedSortBy,
  sanitizedSortDir,
} from "./listParams"
// Prototype-ported visuals (design-fidelity pass) — see the file header of
// studioV2.css for why this is a verbatim, sv2--prefixed port rather than
// Tailwind utilities. Imported here too (not just by the review tree) so the
// `.sv2-root` scoped variables resolve on this page.
import "./review/studio-v2/studioV2.css"

// The five sort keys the server accepts (PR-C3). Trigger/menu-item labels
// reuse the same translation keys the matching table column already renders
// under, except `received_at` — the column header reads "Received"
// (MC_DECL_COL_ARRIVED, the arrival event), while the sort menu names the
// action that produced it ("Uploaded", MC_DECL_V2_LIST_SORT_KEY_RECEIVED) —
// deliberately different words for the same field.
const SORT_OPTIONS: { value: DeclarationSortBy; labelKey: TranslationKeys }[] =
  [
    { value: "supplier", labelKey: "MC_DECL_COL_SUPPLIER" },
    { value: "doc_type", labelKey: "MC_DECL_COL_DOCUMENT" },
    { value: "framework", labelKey: "MC_DECL_V2_LIST_VERORDNUNG" },
    { value: "received_at", labelKey: "MC_DECL_V2_LIST_SORT_KEY_RECEIVED" },
    { value: "status", labelKey: "MC_DECL_COL_STATUS" },
  ]

// Shared trigger look for every facet chip (prototype's `.ftrigger`, restyled
// through Tailwind tokens rather than sv2- classes: these triggers are
// shadcn `Button`s under a Radix Popover, and `cn()`/twMerge only guarantees
// a clean override for Tailwind-vs-Tailwind conflicts — mixing in sv2- rules
// here would be cascade order roulette against the component's own classes.
// The popover panel itself has no exposed className hook, so it keeps the
// app's standard dark popover chrome.
const FACET_TRIGGER_CLASS =
  "h-8 gap-1.5 rounded-lg border-dashed border-grey4 bg-transparent px-[11px] text-[12.5px] font-normal text-white hover:border-grey5 hover:bg-transparent hover:text-white"

// The doc-type sub-line's real value (finding: `Declaration.docType` is a
// wire field NO endpoint actually populates — dead on every row — while
// `pattern` always carries the classified value, and is what this same
// component already searches on, see `hasFacetOrSortActive`'s row filter
// below). Same translation keys `PatternPill` (shared/PatternPill.tsx)
// already uses for this pattern elsewhere, so this needs no new key.
const PATTERN_LABEL_KEYS: Record<DeclarationPattern, TranslationKeys> = {
  "correct-fill": "MC_DECL_PATTERN_CORRECT_FILL",
  "blanket-pdf": "MC_DECL_PATTERN_BLANKET",
  "own-article-numbers": "MC_DECL_PATTERN_OWN_NUMBERS",
  sds: "MC_DECL_PATTERN_SDS",
}

interface ListColumn {
  id: string
  labelKey: TranslationKeys
  /** Omitted for the flexible columns, which share whatever room is left. */
  defaultWidth?: number
}

// The resizable columns, in <thead> order. Ids are single lowercase words:
// they are keys inside the persisted user config, which the API casing
// interceptor rewrites. Document and Regulation are flexible, so at any page
// width they take up the room and Status stays the last column.
const COLUMNS: ListColumn[] = [
  // Leads the row: the row's identity, and the one handle short enough to
  // quote — to a colleague, into the agent chat, or off the supplier's own
  // PDF, which prints this same number as the evidence citation. 125px keeps
  // "DEC-2026-0015" on one line.
  { id: "reference", labelKey: "MC_DECL_COL_REFERENCE", defaultWidth: 125 },
  { id: "supplier", labelKey: "MC_DECL_COL_SUPPLIER", defaultWidth: 120 },
  { id: "document", labelKey: "MC_DECL_COL_DOCUMENT" },
  { id: "regulation", labelKey: "MC_DECL_V2_LIST_VERORDNUNG" },
  // Fits the German header, "EINGEGANGEN".
  { id: "arrived", labelKey: "MC_DECL_COL_ARRIVED", defaultWidth: 125 },
  // Fits the longest common pill, the German "Freigegeben · 120 Artikel".
  { id: "status", labelKey: "MC_DECL_COL_STATUS", defaultWidth: 240 },
]

// Past this, a flexible column stops shrinking and the table scrolls instead.
const FLEXIBLE_COLUMN_MIN_WIDTH = 180

// The trailing delete-button column has no handle and stores no width. While
// a flexible column is left it just holds its button; once every data column
// has been dragged to a width, it takes up the leftover room itself, so each
// data column renders at exactly its stored width (the width the next drag
// starts from) instead of the browser spreading that room over all of them.
const ACTIONS_COLUMN_WIDTH = 56

function sortDirectionLabelKey(
  dir: DeclarationSortDir,
  isDateSort: boolean,
): TranslationKeys {
  if (isDateSort) {
    return dir === "desc"
      ? "MC_DECL_V2_LIST_SORT_LABEL"
      : "MC_DECL_V2_LIST_SORT_OLDEST"
  }
  return dir === "asc" ? "MC_DECL_V2_LIST_SORT_AZ" : "MC_DECL_V2_LIST_SORT_ZA"
}

interface DeclarationListV2Props {
  onOpen: (row: Declaration) => void
  onReview: (row: Declaration) => void
  selectedId?: string
}

export function DeclarationListV2({
  onOpen,
  onReview,
  selectedId,
}: DeclarationListV2Props) {
  const { translate } = useTranslation()
  // The app's language, not the browser's: a German UI in an English browser
  // must still read 6.132.
  const { format: formatCount } = useNumberFormat()
  const [search, setSearch] = useState("")
  // What the server searches on: the box's text once typing settles, so a
  // query is not fired per keystroke.
  const debouncedSearch = useDebouncedValue(search.trim(), 300)
  const [searchParams, setSearchParams] = useSearchParams()
  const qDeclaration = searchParams.get("declaration") ?? ""

  // Read-only deep-link param (no dropdown control) — the dashboard's
  // InReviewWidget links here with `?supplier=...` to drill into a supplier's
  // declarations.
  const supplierFilter = searchParams.get("supplier") ?? ""

  const lifecycleFilter = useMemo(
    () => searchParams.get("lifecycle")?.split(",").filter(Boolean) ?? [],
    [searchParams],
  )
  function setLifecycleFilter(values: string[]) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (values.length > 0) next.set("lifecycle", values.join(","))
      else next.delete("lifecycle")
      return next
    })
  }

  // Read-only deep-link param (no dropdown control) — the dashboard's
  // ReplyQualityWidget links here with `?quality=...` to drill into a
  // reply-quality bucket; see F4-FE.
  const qualityFilter = searchParams.get("quality")

  // The three toolbar facets (PR-C3). Same URL-param convention as
  // `lifecycle` above — one CSV param per facet, so a selection survives a
  // refresh/share the same way lifecycle already does. `supplierIds` holds
  // ids, not names (FacetedFilter's option `value` is the wire value).
  // `supplier_id` (singular) is a read-only deep-link, no facet-chip control:
  // the dashboard's InReviewWidget links here with exactly one id — treated
  // as the facet's initial selection when the URL carries no `supplierIds`
  // of its own.
  const supplierIdsFilter = useMemo(() => {
    const csv = searchParams.get("supplierIds")
    if (csv) return csv.split(",").filter(Boolean)
    const deepLinkId = searchParams.get("supplier_id")
    return deepLinkId ? [deepLinkId] : []
  }, [searchParams])
  function setSupplierIdsFilter(values: string[]) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (values.length > 0) next.set("supplierIds", values.join(","))
      else next.delete("supplierIds")
      // F4: `supplier_id` only ever seeds the facet's initial selection as a
      // fallback when `supplierIds` is absent — leaving it in the URL let it
      // resurrect the very selection the user just changed, on the next
      // render. Any explicit change here supersedes the deep link.
      next.delete("supplier_id")
      return next
    })
  }

  const docTypesFilter = useMemo(
    () => searchParams.get("docTypes")?.split(",").filter(Boolean) ?? [],
    [searchParams],
  )
  function setDocTypesFilter(values: string[]) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (values.length > 0) next.set("docTypes", values.join(","))
      else next.delete("docTypes")
      return next
    })
  }

  const frameworksFilter = useMemo(
    () => searchParams.get("frameworks")?.split(",").filter(Boolean) ?? [],
    [searchParams],
  )
  function setFrameworksFilter(values: string[]) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (values.length > 0) next.set("frameworks", values.join(","))
      else next.delete("frameworks")
      return next
    })
  }

  // Sort (PR-C3). Absent sortBy/sortDir in the URL = the DEFAULT, which is
  // "Eingegangen · Neueste zuerst" ON THE WIRE (product-owner call,
  // 27.08.): the endpoint's own no-sort ordering is `ORDER BY id`, i.e.
  // insertion order oldest-first, so a fresh upload landed on the LAST row
  // (and, past the page cap, not on the first page at all). The URL stays
  // clean for the default so deep links and the filter-active detection
  // (`DeclarationsModule`'s first-run gate) keep reading "no sort chosen".
  const sortBy = sanitizedSortBy(searchParams.get("sortBy"))
  const sortDir = sanitizedSortDir(searchParams.get("sortDir"))
  // The wire defaulting (absent key/direction → received_at desc) lives in
  // `listParamsFromSearch` now, which the hook call below reads straight off
  // `searchParams` — nothing here needs the widened `wireSortBy`/`wireSortDir`
  // any more.
  function setSort(
    nextSortBy: DeclarationSortBy | undefined,
    nextSortDir: DeclarationSortDir | undefined,
  ) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (nextSortBy) next.set("sortBy", nextSortBy)
      else next.delete("sortBy")
      if (nextSortDir) next.set("sortDir", nextSortDir)
      else next.delete("sortDir")
      return next
    })
  }
  function handleSortKeyChange(value: string) {
    if (value === "default") {
      setSort(undefined, undefined)
      return
    }
    const nextSortBy = value as DeclarationSortBy
    setSort(nextSortBy, nextSortBy === DATE_SORT_KEY ? "desc" : "asc")
  }
  // Held blank copies of our own inquiry files (TUR-1754) stay out of the inbox
  // unless this toggle asks for them alone; `listParamsFromSearch` maps it.
  const heldOnly = searchParams.get("held") === "only"
  function setHeldOnly(on: boolean) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (on) next.set("held", "only")
      else next.delete("held")
      return next
    })
  }
  function resetFacetsAndSort() {
    // Search is component-local state (not a URL param) but is one of the
    // things this reset promises to clear — see hasFacetOrSortActive below,
    // which gates the "Reset" affordance on the very same condition.
    setSearch("")
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete("supplierIds")
      // F4: without this, the supplier_id deep-link (still in the URL)
      // resurrected the very selection Reset just cleared, on the next render.
      next.delete("supplier_id")
      next.delete("docTypes")
      next.delete("frameworks")
      next.delete("held")
      next.delete("sortBy")
      next.delete("sortDir")
      return next
    })
  }
  const hasFacetOrSortActive =
    !!search ||
    supplierIdsFilter.length > 0 ||
    docTypesFilter.length > 0 ||
    frameworksFilter.length > 0 ||
    heldOnly ||
    !!sortBy

  const hasUrlFilter = !!(
    supplierFilter ||
    qDeclaration ||
    lifecycleFilter.length > 0 ||
    qualityFilter
  )

  // The search box (`q`) and lifecycle/supplier/quality/supplierIds/docTypes/
  // frameworks/sortBy/sortDir are all server-side filters/sort (the list
  // endpoint is paginated, so filtering client-side after a single page fetch
  // silently drops matches beyond page 1 — see F4-FE; search did exactly
  // that, hiding a match behind "Load more"). Every one of them is part of the query key
  // below (`useDeclarationsWithPolling` keys on the whole params object), so
  // changing any of them starts a fresh infinite query — pagination resets
  // exactly the way it already does for `lifecycle`, with no extra code here.
  // The list itself is paginated (see F3): rows are the flattened set of
  // loaded pages, with "Load more" driving fetchNextPage.
  //
  // `listParamsFromSearch` (TUR-1211), not a locally-built object: it reads
  // the SAME `searchParams` this component already derives its filter chips
  // from, so the review route's own list read (and every "next declaration"
  // jump out of it) can rebuild the identical params off the identical URL,
  // without duplicating this rule set. Search stays out of the URL (it is
  // component-local), so it is added here rather than in that helper.
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useDeclarationsWithPolling({
      ...listParamsFromSearch(searchParams),
      q: debouncedSearch || undefined,
    })

  // Unfiltered, org-scoped inbox totals for the three facet dropdowns — see
  // useDeclarationFacets. Suppliers use `label` as the option label; doc
  // types have no separate label field, so the filter value doubles as its
  // own display label (a pattern id). Frameworks DO carry a separate `code`
  // (mc_framework_codes.normalize_framework_code(value)) — localize through
  // it exactly like the row's own "Verordnung" cell does, falling back to
  // the raw server label when `code` is null/unmapped. `value` (the filter's
  // wire key) is left untouched either way.
  const { data: facets } = useDeclarationFacets()
  const heldCount = facets?.held ?? 0
  const supplierOptions = (facets?.suppliers ?? []).map((s) => ({
    value: s.id,
    label: s.label,
    count: s.count,
  }))
  const docTypeOptions = (facets?.docTypes ?? []).map((d) => ({
    value: d.value,
    label: d.value,
    count: d.count,
  }))
  const frameworkOptions = (facets?.frameworks ?? []).map((f) => ({
    value: f.value,
    label: regulationLabel(f.code, f.value, translate),
    count: f.count,
  }))

  // Status facet options: keep LIFECYCLE_OPTIONS' own order/labels (the
  // hand-picked display order and translation keys), merging in the server's
  // unfiltered inbox count per lifecycle by value. A lifecycle with no facet
  // row yet (e.g. nothing "expiring" today) shows 0, not a missing badge. Any
  // lifecycle value the server returns that ISN'T one of LIFECYCLE_OPTIONS
  // (a backend state added before this list learned its label) is appended
  // at the end rather than dropped, falling back to the raw value as its own
  // label — the same fallback docType/framework options above already use.
  const lifecycleCounts = new Map(
    (facets?.lifecycle ?? []).map((l) => [l.value, l.count]),
  )
  const lifecycleOptions = [
    ...LIFECYCLE_OPTIONS.map((o) => ({
      value: o.value,
      label: translate(o.labelKey),
      count: lifecycleCounts.get(o.value) ?? 0,
    })),
    ...(facets?.lifecycle ?? [])
      .filter((l) => !LIFECYCLE_OPTIONS.some((o) => o.value === l.value))
      .map((l) => ({ value: l.value, label: l.value, count: l.count })),
  ]

  // Server sort always wins: every request carries a sort (the default is
  // received_at desc, see `listParamsFromSearch`), so the rows arrive
  // ordered and stable across "Load more" pages. The server is the only
  // thing that sees every page (not just what's loaded here), so this is
  // never re-sorted —
  // see PR #2014 (main): v1's full lifecycle bucketing
  // (`sortDeclarationsForDisplay`, still used by DeclarationList) fought that
  // server order wholesale rather than just pinning in-flight rows, and a
  // page-2+ row could reorder relative to rows already on screen.
  const rows = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data])

  // Same-page complement to the notification centre's server-side "ready for
  // review" alert (see hook doc): fires on a status TRANSITION this mount
  // actually observed, e.g. a poll flipping a row from processing to ready
  // while the reviewer is looking at the list. Mounted here ONLY — never on
  // DeclarationReviewPage, which mounts the very same polling query and would
  // otherwise double-toast every transition it also observes. Fed `rows`
  // (every loaded row), not `filtered`. A search narrows `rows` the same way a
  // facet does, and the hook forgets a row that leaves and treats its return
  // as a first observation, so a search never fakes a transition.
  useDeclarationLifecycleToasts(rows)

  // A column renders at the width it was dragged to, else at its default.
  // Only dragged widths are stored, so a later change to a default still
  // reaches everyone who never resized that column.
  const [columnWidths, setColumnWidths] = useMcTableColumnWidths(
    "supplierDeclarationsStudioList",
  )
  const widthOf = ({ id, defaultWidth }: ListColumn) =>
    columnWidths[id] ?? defaultWidth
  const hasFlexibleColumn = COLUMNS.some(
    (column) => widthOf(column) === undefined,
  )
  const tableMinWidth = COLUMNS.reduce(
    (sum, column) => sum + (widthOf(column) ?? FLEXIBLE_COLUMN_MIN_WIDTH),
    ACTIONS_COLUMN_WIDTH,
  )

  // Row-level delete, mirroring the import history's per-row control. Approved
  // declarations ("accepted"/"expiring"/"expired") have written master data
  // naming them as evidence — the backend 409s their delete — so those rows
  // get no button.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const retryMutation = useRetryDeclaration()
  // Row-level permission checks — buttons are HIDDEN (not disabled) when the
  // member lacks the write key. Backend + hook-net catch any bypass.
  const { hasPermission } = useUser()
  const canRetry = hasPermission(PERMISSIONS.MC_DECLARATIONS_RETRY)
  const canDeleteAny = hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)
  // Per-row in-flight tracking so multiple concurrent retries each spin/disable
  // independently (useMutation only exposes the single latest `variables`).
  const [retryingIds, setRetryingIds] = useState<Set<string>>(() => new Set())
  const triggerRetry = (id: string) => {
    setRetryingIds((prev) => new Set(prev).add(id))
    retryMutation.mutate(id, {
      onSuccess: () => {
        toast.success(translate("MC_DECL_V2_RETRY_TOAST_SUCCESS"))
      },
      onError: (error) => {
        toastApiError(error, translate, "MC_DECL_V2_RETRY_TOAST_ERROR")
      },
      onSettled: () =>
        setRetryingIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        }),
    })
  }

  // Deep-link from the "declaration_processing_failed" notification's "Try again"
  // CTA: /…/declarations?retry=<id> auto-triggers reprocessing for that row, then
  // clears the param so it can't re-fire on refresh/back.
  const retryParam = searchParams.get("retry")
  const autoRetriedRef = useRef(false)
  useEffect(() => {
    if (!retryParam || autoRetriedRef.current) return
    const row = rows.find((r) => r.id === retryParam)
    if (!row) return // rows may still be loading — wait for it to arrive
    autoRetriedRef.current = true
    if (row.lifecycleStatus !== "processing") {
      triggerRetry(retryParam)
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("retry")
        return next
      },
      { replace: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryParam, rows])

  // Clear the URL-driven filter (deep-link params) and show the full list.
  // The local search box is intentionally separate and left untouched.
  const clearUrlFilters = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete("declaration")
      next.delete("supplier")
      next.delete("lifecycle")
      next.delete("quality")
      return next
    })
  }

  // THE TILES COUNT THE INBOX, NOT THE PAGE — server-side counts over every
  // declaration, unaffected by scroll position and by the table's own filters.
  // See DeclarationList.tsx for the fuller history of that fix; the reviewbar
  // below reuses the very same `awaitingCount` rather than recounting it.
  const { data: stats } = useDeclarationStats()
  // `stats` is undefined only before the first response lands — after that,
  // even an error/empty result resolves to the zeroed DeclarationStats shape
  // (see getDeclarationStats' catch). The reviewbar below reads this to avoid
  // flashing "all done" (0 awaiting) during that initial window.
  const statsLoaded = stats !== undefined
  const awaitingCount = stats?.awaitingReview ?? 0
  const processingCount = stats?.inProgress ?? 0
  const acceptedCount = stats?.acceptedLast30d ?? 0
  const expiringCount = stats?.expiringWithin30d ?? 0

  // The reviewbar's "Prüfung starten" targets the same route a row click does
  // (onReview), on the first 'ready' row among the LOADED rows, which an
  // active search or facet narrows like any server filter. `awaitingCount` is a server-wide
  // count and the table is paginated, so a 'ready' row can legitimately not be
  // loaded yet on first render; the button disables rather than navigating nowhere.
  const firstReadyRow = rows.find(
    (r) => r.lifecycleStatus === "ready" && !r.roundtripHold,
  )

  // `awaitingCount` (server-wide) and `firstReadyRow` (loaded page only) can
  // legitimately disagree — an active facet/status filter excludes every
  // ready row from what's loaded, or a ready row simply hasn't been
  // paginated in yet. Disabling with no reason then reads as a dead button
  // even though the count above it says otherwise. Two distinguishable
  // causes, two distinct explanations, so the button's `title` always says
  // why rather than just going quiet. (Chose disable+explain over
  // auto-clearing filters and walking pages until a ready row turns up:
  // that would mean silently discarding the user's own filter choice and an
  // open-ended fetch loop with no bound on how many pages to walk — a much
  // larger, riskier change for what "Prüfung starten" needs to do here.)
  const hasActiveFacetOrStatusFilter =
    !!debouncedSearch ||
    supplierIdsFilter.length > 0 ||
    docTypesFilter.length > 0 ||
    frameworksFilter.length > 0 ||
    lifecycleFilter.length > 0
  const reviewCtaDisabledReason = firstReadyRow
    ? undefined
    : hasActiveFacetOrStatusFilter
      ? translate("MC_DECL_V2_LIST_REVIEWBAR_CTA_FILTERED")
      : translate("MC_DECL_V2_LIST_REVIEWBAR_CTA_LOADING")

  // Table-only ordering: pin in-flight (pending/processing) rows first when
  // no explicit sort is active, so a fresh upload never reads as lost behind
  // older accepted/expiring rows on the same page — see `pinInFlightRows`.
  // Once a sort is active the server's own order is authoritative and stays
  // untouched. `rows` itself (unpinned) still feeds everything that isn't the
  // table body — the lifecycle-toast hook above, `firstReadyRow`, and the
  // retry deep-link effect all read `rows`, not this.
  const displayRows = useMemo(
    () => (sortBy ? rows : pinInFlightRows(rows)),
    [rows, sortBy],
  )

  // Search and lifecycle/supplier/quality are applied server-side (see
  // useDeclarationsWithPolling above) so they must NOT be re-filtered here:
  // the server's matches are not always visible in the row's own fields (it
  // searches the stored framework metadata, the row shows the extracted
  // framework label), and a second pass would hide those matches again. Only
  // the (still unwired) `declaration` deep-link param applies client-side.
  const filtered = displayRows.filter(
    (r) =>
      !qDeclaration ||
      (r.doc ?? "").toLowerCase().includes(qDeclaration.toLowerCase()),
  )

  // Gate for the "no matches" empty-state row below: only when a filter or
  // search could plausibly be why the table is empty — not when the org
  // genuinely has zero declarations, which is a different (unfiltered)
  // empty state this component doesn't own an opinion on — and only once
  // the first response has actually landed, so a still-loading table never
  // flashes "no matches" before its real rows arrive.
  const isFilteringActive =
    !!search ||
    supplierIdsFilter.length > 0 ||
    docTypesFilter.length > 0 ||
    frameworksFilter.length > 0 ||
    hasUrlFilter
  // `filtered` only ever covers the LOADED pages — with more pages still to
  // fetch (`hasNextPage`), an unloaded page could still contain a match for
  // the active search/facets. Asserting "no matches" next to a "Load more"
  // button that might produce one would be its own version of finding 4's
  // bug, so hold off until every page is in.
  const showEmptyFilteredState =
    isFilteringActive && !isLoading && !hasNextPage && filtered.length === 0
  // The empty row's reset must actually empty the table's cause, not just
  // some of it — facets/sort/search AND the deep-link params (supplier,
  // lifecycle, quality, declaration) all filter server- or client-side, so
  // clearing only one set could leave the row showing with nothing fixed.
  // A single setSearchParams call (not resetFacetsAndSort() + clearUrlFilters()
  // called back to back): both would resolve their updater against the SAME
  // stale, render-time `searchParams`, so the second call's `next` would win
  // and silently resurrect whatever the first one deleted.
  function resetAllFilters() {
    setSearch("")
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete("supplierIds")
      next.delete("docTypes")
      next.delete("frameworks")
      next.delete("held")
      next.delete("sortBy")
      next.delete("sortDir")
      next.delete("declaration")
      next.delete("supplier")
      next.delete("supplier_id")
      next.delete("lifecycle")
      next.delete("quality")
      return next
    })
  }

  const activeSortOption = SORT_OPTIONS.find((o) => o.value === sortBy)
  const isDateSort = sortBy === DATE_SORT_KEY
  const firstDir: DeclarationSortDir = isDateSort ? "desc" : "asc"
  const secondDir: DeclarationSortDir = isDateSort ? "asc" : "desc"
  const effectiveSortDir = sortDir ?? firstDir
  const sortTriggerLabel = activeSortOption
    ? `${translate(activeSortOption.labelKey)} · ${translate(sortDirectionLabelKey(effectiveSortDir, isDateSort))}`
    : translate("MC_DECL_V2_LIST_SORT_TRIGGER")

  return (
    <div className="sv2-root">
      <div className="mx-auto flex max-w-[1120px] flex-col gap-[18px]">
        {/* No pagehead here: the headline is DeclarationsModule's standard
            ModuleHeader, same as every other module — the prototype's
            oversized h1 shipped briefly and was reverted on the product
            owner's explicit call ("wie bei allen anderen Modulen"). */}

        {/* Reviewbar — same awaiting-review count as the first KPI tile below,
            reused rather than recounted. "Prüfung starten" opens the same route a
            row click does, via the shared onReview callback. */}
        <div
          className={cn(
            "sv2-reviewbar",
            statsLoaded && awaitingCount === 0 && "sv2-empty",
          )}
        >
          {statsLoaded ? (
            <>
              <div className="sv2-txt">
                <div className="sv2-h">
                  {awaitingCount > 0
                    ? translate("MC_DECL_V2_LIST_REVIEWBAR_COUNT", {
                        n: awaitingCount,
                      })
                    : translate("MC_DECL_V2_LIST_REVIEWBAR_EMPTY")}
                </div>
              </div>
              {awaitingCount > 0 &&
                // The explanatory `title` is wrapped on a <span> rather than
                // placed on the <button> itself — a native `disabled` button
                // doesn't reliably fire hover in every browser (it's already
                // excluded from the tab order and from click), which would
                // make a `title` on the button silently inert. See
                // ReviewMaskPane's own "add statement" tooltip for the same
                // span-wrap pattern used elsewhere in this tree.
                (reviewCtaDisabledReason ? (
                  <span title={reviewCtaDisabledReason}>
                    <button
                      type="button"
                      className="sv2-btn sv2-btn-primary"
                      disabled
                    >
                      {translate("MC_DECL_V2_LIST_REVIEWBAR_CTA")}
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="sv2-btn sv2-btn-primary"
                    onClick={() => {
                      if (firstReadyRow) onReview(firstReadyRow)
                    }}
                  >
                    {translate("MC_DECL_V2_LIST_REVIEWBAR_CTA")}
                  </button>
                ))}
            </>
          ) : (
            // Stats haven't resolved yet — showing neither the CTA nor the
            // all-done state avoids a false "nothing to review" flash before
            // the real awaitingCount (which may be > 0) arrives.
            <Skeleton className="h-4 w-56" />
          )}
        </div>

        {/* KPI row — deliberate product addition on top of the prototype
            (server-counted stats; the prototype has none). StatTile's
            grey3/grey4 tokens are value-identical to this page's --bg-app/
            --line, so it already sits correctly against the sv2 background —
            left as-is rather than duplicating a shared component locally. */}
        <StatGrid
          items={[
            {
              label: translate("MC_DECL_KPI_AWAITING"),
              value: awaitingCount,
              icon: FileText,
              tone: awaitingCount > 0 ? "warning" : "default",
            },
            {
              label: translate("MC_DECL_KPI_PROCESSING"),
              value: processingCount,
              icon: Loader2,
            },
            {
              label: translate("MC_DECL_KPI_ACCEPTED30"),
              value: acceptedCount,
              icon: CheckCircle,
              tone: "success",
            },
            {
              label: translate("MC_DECL_KPI_EXPIRING30"),
              value: expiringCount,
              icon: Clock,
            },
          ]}
        />

        {/* Toolbar: search + status + the three facets (Lieferant, Dokument,
            Verordnung) + sort, all server-side (PR-C3). Every facet/sort change
            lands in the URL the same way `lifecycle` already does, so the list
            hook's params object — and with it the query key and pagination —
            picks it up with no extra wiring here. */}
        <div className="sv2-toolbar">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={translate("MC_DECL_SEARCH_PLACEHOLDER")}
            onClear={() => setSearch("")}
          />
          {/* Chip order is the prototype's: Lieferant, Dokument, Verordnung,
              Status — mirroring the table's own column order. */}
          <FacetedFilter
            title={translate("MC_DECL_COL_SUPPLIER")}
            options={supplierOptions}
            selected={supplierIdsFilter}
            onSelectionChange={setSupplierIdsFilter}
            triggerClassName={FACET_TRIGGER_CLASS}
          />

          <FacetedFilter
            title={translate("MC_DECL_COL_DOCUMENT")}
            options={docTypeOptions}
            selected={docTypesFilter}
            onSelectionChange={setDocTypesFilter}
            triggerClassName={FACET_TRIGGER_CLASS}
          />

          <FacetedFilter
            title={translate("MC_DECL_V2_LIST_VERORDNUNG")}
            options={frameworkOptions}
            selected={frameworksFilter}
            onSelectionChange={setFrameworksFilter}
            triggerClassName={FACET_TRIGGER_CLASS}
          />

          <FacetedFilter
            title={translate("MC_DECL_FB_STATUS")}
            options={lifecycleOptions}
            selected={lifecycleFilter}
            onSelectionChange={setLifecycleFilter}
            triggerClassName={FACET_TRIGGER_CLASS}
          />

          {(heldCount > 0 || heldOnly) && (
            <Button
              variant="outline"
              size="sm"
              className={cn(FACET_TRIGGER_CLASS, heldOnly && "border-solid")}
              aria-pressed={heldOnly}
              title={translate("MC_DECL_V2_LIST_HELD_HINT")}
              onClick={() => setHeldOnly(!heldOnly)}
            >
              {translate("MC_DECL_V2_LIST_HELD")}
              <span className="sv2-fbadge">{formatCount(heldCount)}</span>
            </Button>
          )}

          {/* Sort — shows the active sort inline ("Sortiert: Hochgeladen ·
              Neueste zuerst") instead of a bare trigger, per the prototype's
              sortControl(); the value itself renders in an sv2-fbadge chip,
              which is safe to sv2-style directly since it's a plain span this
              component owns (not a shadcn Button). */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(FACET_TRIGGER_CLASS, sortBy && "border-solid")}
              >
                <ArrowUpDown className="size-[15px] shrink-0 text-grey5" />
                {activeSortOption ? (
                  <>
                    {translate("MC_DECL_V2_LIST_SORT_ACTIVE_PREFIX")}
                    <span className="sv2-fbadge">{sortTriggerLabel}</span>
                  </>
                ) : (
                  sortTriggerLabel
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuRadioGroup
                value={sortBy ?? "default"}
                onValueChange={handleSortKeyChange}
              >
                <DropdownMenuRadioItem value="default">
                  {translate("MC_DECL_V2_LIST_SORT_DEFAULT")}
                </DropdownMenuRadioItem>
                {SORT_OPTIONS.map((option) => (
                  <DropdownMenuRadioItem
                    key={option.value}
                    value={option.value}
                  >
                    {translate(option.labelKey)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              {sortBy && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup
                    value={effectiveSortDir}
                    onValueChange={(value) =>
                      setSort(sortBy, value as DeclarationSortDir)
                    }
                  >
                    <DropdownMenuRadioItem value={firstDir}>
                      {translate(sortDirectionLabelKey(firstDir, isDateSort))}
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value={secondDir}>
                      {translate(sortDirectionLabelKey(secondDir, isDateSort))}
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {hasFacetOrSortActive && (
            <button
              type="button"
              onClick={resetFacetsAndSort}
              className="text-[12px] font-medium text-grey5 underline-offset-2 transition-colors hover:text-white hover:underline"
            >
              {translate("MC_DECL_V2_LIST_RESET")}
            </button>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {hasUrlFilter && (
            <div className="flex min-w-0 items-center gap-1.5 text-[11.5px] text-grey5">
              <Filter size={11} className="shrink-0" />
              <span className="truncate">
                {translate("MC_DECL_FILTER_PREFIX")}:{" "}
                {[
                  supplierFilter,
                  qDeclaration,
                  lifecycleFilter.join(", "),
                  qualityFilter,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
              <span className="text-grey4">·</span>
              <button
                type="button"
                onClick={clearUrlFilters}
                className="shrink-0 text-grey5 underline-offset-2 transition-colors hover:text-white hover:underline"
              >
                {translate("MC_DECL_FILTER_CLEAR")}
              </button>
            </div>
          )}

          {/* Table — prototype's LIEFERANT | DOKUMENT | VERORDNUNG | HOCHGELADEN
              | STATUS columns (plus a quiet actions column this workspace
              needs and the prototype doesn't: row delete). Hand-rolled with
              sv2- classes rather than McTable/McTd — McTable's own Tailwind
              cell classes and `.sv2-root td`'s type+class selector are both
              specificity (0,1,1), so mixing the two is source-order roulette,
              not a real override. */}
          <div className="sv2-tablewrap">
            <table style={{ width: `max(100%, ${tableMinWidth}px)` }}>
              <colgroup>
                {COLUMNS.map((column) => (
                  <col key={column.id} style={{ width: widthOf(column) }} />
                ))}
                <col
                  style={{
                    width: hasFlexibleColumn ? ACTIONS_COLUMN_WIDTH : undefined,
                  }}
                />
              </colgroup>
              <thead>
                <tr>
                  {COLUMNS.map(({ id, labelKey }) => {
                    const label = translate(labelKey)
                    // Named explicitly, like McTable's headers: otherwise the
                    // resize handle's label joins the header's accessible name.
                    return (
                      <th key={id} aria-label={label}>
                        {label}
                        {/* Stored widths only: a column at its default or
                            flexible width starts a resize from its rendered
                            width, and stays unstored until it is resized. */}
                        <ColumnResizeHandle
                          columnId={id}
                          columnLabel={label}
                          columnWidths={columnWidths}
                          onColumnWidthsChange={setColumnWidths}
                        />
                      </th>
                    )
                  })}
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isPending = r.lifecycleStatus === "pending"
                  const isProcessing = r.lifecycleStatus === "processing"
                  const isReady = r.lifecycleStatus === "ready"
                  const isAccepted = r.lifecycleStatus === "accepted"
                  const isExpiring = r.lifecycleStatus === "expiring"
                  const isExpired = r.lifecycleStatus === "expired"
                  const isLegacy = !r.lifecycleStatus
                  // The backend fills the scope on BOTH in-flight lifecycles
                  // (pending and processing), so the large-scope explanation
                  // belongs on either badge — a big pending row must not spin
                  // without saying why.
                  const inFlightTitle = r.largeScope
                    ? translate("MC_DECL_PROCESSING_LARGE_SCOPE_BODY", {
                        count: formatCount(r.scopeArticleCount ?? 0),
                      })
                    : translate("MC_DECL_ROW_BEING_READ")
                  const isFailed = r.lifecycleStatus === "failed"
                  const hold = holdReasonOf(r)
                  // The empty-extraction badge is a fallback signal: on rows that
                  // already wear a lifecycle badge (ready/accepted/expiring/expired)
                  // it stacked as a second pill on a second line — the lifecycle
                  // badge carries the actionable state, so it renders alone.
                  const isExtractionEmpty =
                    r.lifecycleStatus !== "failed" &&
                    r.lifecycleStatus !== "ready" &&
                    !isPostApproval(r.lifecycleStatus) &&
                    !!r.extractionEmpty
                  const isRetrying = retryingIds.has(r.id)
                  const canDelete =
                    canDeleteAny && !isPostApproval(r.lifecycleStatus)

                  // A PROCESSING ROW OPENS THE REVIEW. It used to be dead — the review
                  // route bounced it — and the reviewer's own document being read is
                  // exactly what they want to watch, so the row leads to the live board.
                  // A PENDING (queued) ROW OPENS IT TOO (B5) — it used to be dead as
                  // well, landing the reviewer nowhere; the review route now shows an
                  // honest waiting state for it (StudioV2Layout's awaitingExtraction
                  // branch), so there is somewhere real to send the click. `failed`
                  // keeps its retry as the only sensible gesture; the row stays
                  // otherwise inert (retry is a dedicated button, not the row click).
                  const handleRowClick = isFailed
                    ? undefined
                    : isReady || isProcessing || isPending
                      ? () => onReview(r)
                      : () => onOpen(r)

                  return (
                    <tr
                      key={r.id}
                      onClick={handleRowClick}
                      className={cn(
                        handleRowClick && "sv2-clickable",
                        r.id === selectedId &&
                          "bg-yellow1/6 outline-1 outline-yellow1/30 outline-solid",
                      )}
                    >
                      {/* Monospace so a column of references aligns and scans
                          as one; an em dash for a row from before the column
                          existed, never a blank that reads as a load failure. */}
                      <td className="font-mono text-[12px] text-grey5">
                        {r.declarationNumber ?? "—"}
                      </td>
                      <td className="sv2-cprimary">
                        {r.supplier}
                        {/* MC_DECL_V2_SUP — the pipeline detected an issuer
                            master data does not know yet. A quiet dot, not a
                            filled pill: the cell stays primary text. Empty
                            supplier stays exactly as before (no dot on
                            nothing). */}
                        {r.supplierMatched === false && r.supplier && (
                          <>
                            <span
                              className="sv2-supdot"
                              title={translate(
                                "MC_DECL_V2_SUP_UNMATCHED_TITLE",
                              )}
                            />
                            <span className="ml-1.5 text-[10px] font-normal text-grey5">
                              {translate("MC_DECL_V2_SUP_UNMATCHED_LABEL")}
                            </span>
                          </>
                        )}
                      </td>
                      <td>
                        <div className="sv2-doccell">
                          <div>
                            <div className="sv2-nm">{r.doc}</div>
                            {/* `r.docType` first: kept as a live fallback in
                                case a future endpoint build populates it
                                (the field stays on the wire type) — but no
                                endpoint does today, so this reads `r.pattern`
                                (always populated) via the SAME labels
                                `PatternPill` shows elsewhere. `PATTERN_LABEL_KEYS[r.pattern]`
                                can only miss for a pattern value newer than
                                this FE build's enum — renders nothing rather
                                than crashing on an untranslatable key. */}
                            {(r.docType ??
                              (PATTERN_LABEL_KEYS[r.pattern]
                                ? translate(PATTERN_LABEL_KEYS[r.pattern])
                                : null)) && (
                              <div className="sv2-ty">
                                {r.docType ??
                                  translate(PATTERN_LABEL_KEYS[r.pattern])}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* First Verordnung + grey "+N", the prototype's regcell
                          (`${c.reg} +${n-1}`). The server joins the detected
                          labels with ", " (declaration_interface.frameworks_label);
                          splitting on the same separator is that join in reverse.
                          The full list stays reachable as the cell's title. */}
                      <td
                        className="sv2-regcell"
                        title={r.framework ?? undefined}
                      >
                        {(() => {
                          const labels = (r.framework ?? "")
                            .split(", ")
                            .filter(Boolean)
                          if (labels.length === 0) return null
                          const displayLabels = regulationLabels(
                            r.frameworkCodes,
                            labels,
                            translate,
                          )
                          return (
                            <>
                              {displayLabels[0]}
                              {displayLabels.length > 1 && (
                                <span style={{ color: "var(--fg3)" }}>
                                  {" "}
                                  +{displayLabels.length - 1}
                                </span>
                              )}
                            </>
                          )
                        })()}
                      </td>
                      <td className="sv2-datecell">
                        {formatDate(r.capturedAt) || "—"}
                      </td>
                      <td>
                        {isFailed && (
                          // Wraps the Retry button under the pill rather than
                          // clipping it in a narrow Status column.
                          <span className="inline-flex flex-wrap items-center gap-2">
                            <span className="sv2-tpill sv2-fail">
                              <span className="sv2-d" />
                              {translate("MC_DECL_BADGE_FAILED")}
                            </span>
                            {canRetry && (
                              <button
                                type="button"
                                disabled={isRetrying}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  triggerRetry(r.id)
                                }}
                                className="inline-flex items-center gap-1.5 rounded-md border border-grey4 bg-white/4 px-2.5 py-1 text-[12px] font-medium text-grey5 transition-opacity hover:bg-white/8 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <RefreshCw
                                  size={12}
                                  className={isRetrying ? "animate-spin" : ""}
                                />
                                {translate("MC_DECL_BTN_RETRY")}
                              </button>
                            )}
                          </span>
                        )}
                        {isPending && (
                          // B1: same live-spinner markup as isProcessing below
                          // (not the static "sv2-d" dot other badges use) — a
                          // pending row is already in-flight, just not yet
                          // picked up, and reads that way now instead of as
                          // inert storage.
                          <span
                            className="sv2-tpill sv2-open"
                            title={inFlightTitle}
                          >
                            <span className="size-[9px] shrink-0 animate-spin rounded-full border-2 border-grey4 border-t-yellow1" />
                            {translate("MC_DECL_BADGE_PENDING")}
                          </span>
                        )}
                        {isExtractionEmpty && (
                          <span
                            className="sv2-tpill sv2-open"
                            title={translate("MC_DECL_BADGE_EMPTY")}
                          >
                            <span className="sv2-d" />
                            {translate("MC_DECL_BADGE_EMPTY")}
                          </span>
                        )}
                        {isProcessing && (
                          <span
                            className="sv2-tpill sv2-open"
                            title={inFlightTitle}
                          >
                            <span className="size-[9px] shrink-0 animate-spin rounded-full border-2 border-grey4 border-t-yellow1" />
                            {translate("MC_DECL_BADGE_PROCESSING")}…
                          </span>
                        )}
                        {/* TUR-1452: a held declaration is always 'ready'
                            (processed, never approved) with needsCount 0,
                            which the branch below would render as an
                            indistinguishable "0 zu freigeben, alles
                            erledigt" pill. This is exactly the row that has
                            nothing to approve for a reason the reviewer
                            needs to see. */}
                        {isReady && hold ? (
                          <RoundtripHoldPill reason={hold} />
                        ) : (
                          isReady &&
                          (() => {
                            // NOTHING OUTSTANDING IS NOT A WARNING. Every ready row wore
                            // the same amber dot whatever its count, so a declaration
                            // with no open items looked identical to one with twelve and
                            // the colour carried no information at all.
                            const n = r.needsCount ?? 0
                            const label = translate("MC_DECL_BADGE_NEED_YOU", {
                              n,
                            })
                            return (
                              <span
                                className={cn(
                                  "sv2-tpill",
                                  n > 0 ? "sv2-warn" : "sv2-done",
                                )}
                                title={label}
                              >
                                <span className="sv2-d" />
                                {label}
                              </span>
                            )
                          })()
                        )}
                        {isAccepted && (
                          <span className="sv2-tpill sv2-done">
                            <span className="sv2-d" />
                            {r.acceptedArticleCount
                              ? translate("MC_DECL_BADGE_ACCEPTED_ART", {
                                  n: r.acceptedArticleCount,
                                })
                              : translate("MC_DECL_BADGE_ACCEPTED")}
                          </span>
                        )}
                        {isExpiring && (
                          <span
                            className="sv2-tpill sv2-warn"
                            title={translate("MC_DECL_BADGE_EXPIRING", {
                              n: r.expiresInDays ?? 0,
                            })}
                          >
                            <span className="sv2-d" />
                            {translate("MC_DECL_BADGE_EXPIRING", {
                              n: r.expiresInDays ?? 0,
                            })}
                          </span>
                        )}
                        {isExpired && (
                          <span
                            className="sv2-tpill sv2-fail"
                            title={translate("MC_DECL_BADGE_EXPIRED", {
                              n: Math.abs(r.expiresInDays ?? 0),
                            })}
                          >
                            <span className="sv2-d" />
                            {translate("MC_DECL_BADGE_EXPIRED", {
                              n: Math.abs(r.expiresInDays ?? 0),
                            })}
                          </span>
                        )}
                        {isLegacy &&
                          (() => {
                            const isProcessed =
                              r.lifecycleStatus !== undefined
                                ? r.lifecycleStatus === "ready" ||
                                  isPostApproval(r.lifecycleStatus)
                                : r.handoff === "done"
                            return isProcessed ? (
                              <span className="sv2-tpill sv2-done">
                                <span className="sv2-d" />
                                {translate("MC_DECL_STATUS_PROCESSED")}
                              </span>
                            ) : (
                              <span className="sv2-tpill sv2-open">
                                <span className="sv2-d" />
                                {translate("MC_DECL_STATUS_PENDING")}
                              </span>
                            )
                          })()}
                      </td>
                      {/* Right-aligned: once every data column has a stored
                          width this column takes up the room left over, and
                          the button stays at the table's right edge. */}
                      <td className="w-[44px] text-right">
                        {canDelete && (
                          <button
                            type="button"
                            aria-label={translate("MC_DECL_DELETE_BTN")}
                            title={translate("MC_DECL_DELETE_BTN")}
                            onClick={(e) => {
                              e.stopPropagation()
                              setConfirmDeleteId(r.id)
                            }}
                            className="sv2-rowdel inline-flex size-7 items-center justify-center rounded-md text-grey5 transition-colors hover:bg-red1/10 hover:text-red1"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {showEmptyFilteredState && (
                  // filtered.length === 0 with a filter/search active used to
                  // render nothing here — a silently empty table indistinguishable
                  // from a slow load. Six columns: Supplier, Document, Verordnung,
                  // Arrived, Status, actions (see <thead> above).
                  <tr>
                    <td colSpan={7} className="py-10 text-center">
                      <p className="text-[13px] text-grey5">
                        {translate("MC_DECL_V2_LIST_EMPTY_FILTERED")}
                      </p>
                      <button
                        type="button"
                        onClick={resetAllFilters}
                        className="mt-2 text-[12px] font-medium text-grey5 underline-offset-2 transition-colors hover:text-white hover:underline"
                      >
                        {translate("MC_DECL_V2_LIST_RESET")}
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {hasNextPage && (
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="animate-in self-center rounded-md border border-grey4 bg-white/4 px-4 py-1.5 text-[12px] font-medium text-grey5 transition-colors fade-in hover:bg-white/8 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {translate("MC_DECL_LOAD_MORE")}
            </button>
          )}

          {/* No import button down here: the action lives in
              DeclarationsModule's standard ModuleHeader (top right), like in
              every other module — the prototype's below-the-table ghost
              button was reverted on the product owner's call together with
              the oversized pagehead. */}
        </div>
      </div>
      <DeclarationDeleteDialog
        declarationId={confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
      />
    </div>
  )
}
